import { DOCUMENT } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { WINDOW } from '@ng-web-apis/common';
import { Store } from '@ngrx/store';
import { Observable, Subject, of } from 'rxjs';
import { v4 } from 'uuid';

import { CountdownService } from '../countdown/countdown.service';

import { BrowserOnly } from '@core/data-providers/platform/platform.decorators';
import { CommonWindow } from '@core/domain/models/common-window.model';
import { EnvironmentCmsUrls } from '@core/domain/types/environment-config.types';
import { ConfigService } from '@core/services/config/config.service';
import { HttpRequestsService, RequestParam, RequestParams } from '@core/services/http-requests/http-requests.service';
import { TranslationService } from '@core/services/translation.service';
import { CountDownByConfigType } from '@features/cms-render/domain/constants/cms-render.constants';
import { CmsResponse } from '@features/cms-render/domain/models/cmsResponse';
import { ContentItem } from '@features/cms-render/domain/models/contentItem';
import { ContentItemTree } from '@features/cms-render/domain/models/contentItemTree';
import {
    CheckLinkType,
    LoadPieceProductCarouselModule,
    LoadSlug,
    RemoveAllSlugs, ShowCountdown,
} from '@features/cms-render/domain/state/cms-render.actions';
import { CountdownDate } from '@features/cms-render/ui/render-pieces/piece-countdown/piece-countdown.model';
import { CmsRenderStylesUtils } from '@features/cms-render/ui/services/cms-render/cms-render-styles.utils';
import { DuttiDate } from '@frontend/common/duttidate';
import { deepClone } from '@frontend/common/utils';
import { LoadAccessibility } from '@shared/accessibility/store/accessibility.actions';
import { EventFrom } from '@shared/analytics/COMMON/models/analytics-common.models';
import { PageViewEvent } from '@shared/analytics/store/analytics.actions';
import { ClickOnCmsComponentAnalytics } from '@shared/analytics/store/app-events-analytics.actions';
import { OpenDownloadAppLink } from '@shared/banners-system/banners/banner-download-app/store/banner-download.actions';
import { CloseBanner } from '@shared/banners-system/store/banners-system.actions';
import { OpenSearchFromHeader } from '@shared/header/store/header.actions';
import { NoOpAction, OpenCategoriesInHeader } from '@shared/menu-categories/store/menu-categories.actions';
import {
    GoBack,
    GoTo,
    GoToCategory,
    GoToExternalPage,
    GoToInternalPage,
    GoToProduct,
    GoToSection,
} from '@shared/navigation/store/navigation.actions';

@Injectable()
export class CmsRenderService {
    private readonly $sliceCmsEvent: Subject<any> = new Subject();

    public sliceCmsEvent = this.$sliceCmsEvent.asObservable();

    constructor(
        private readonly configService: ConfigService,
        private readonly http: HttpClient,
        private readonly requestService: HttpRequestsService,
        private readonly store: Store,
        private readonly countDownService: CountdownService,
        private readonly route: ActivatedRoute,
        @Inject(WINDOW) private readonly window: CommonWindow,
        @Inject(DOCUMENT) private readonly document: Document,
    ) { }

    public getPage(slug: string, country: string, lang: string, customParams?: RequestParams): Observable<CmsResponse> {
        if (!slug || slug === 'null' || slug === 'undefined') {
            return of(null);
        }
        const language = TranslationService.fixLangCode(lang);

        const url = this.requestService.requestCms(`${slug}`, country, language, customParams);
        return this.http.get<CmsResponse>(url);
    }

    public previewPage(params: string): Observable<any> {
        const url = this.requestService.requestPreviewCms(params);
        return this.http.get<CmsResponse>(url);
    }

    @BrowserOnly<CmsRenderService, null>(null)
    public sendSegmentToParent(segmentId: string) {
        const targetOrigin = this.configService.getConfigUrl<EnvironmentCmsUrls>('cmsUrls').cmsDashboardBaseUrl;
        this.window.parent.postMessage({ type: 'cms', data: { segmentId } }, targetOrigin);
    }

    // Replace old Helvetica fonts with new dutti fonts
    public replaceFonts(text: string): string {
        const html = JSON.stringify(text);

        const duttiFontsMapObj = {
            'HelveticaNeueLTPro-Lt': 'dutti-daily-regular, Helvetica, arial, sans-serif',
            'HelveticaNeueLTPro-Roman': 'dutti-daily-regular, Helvetica, arial, sans-serif',
            'HelveticaNeueLTPro-Cn': 'dutti-daily-bold, Helvetica, arial, sans-serif',
            'HelveticaNeueLTPro-MdCn': 'dutti-daily-bold, Helvetica, arial, sans-serif',
            'HelveticaNeueLTPro-BdCn': 'dutti-daily-bold, Helvetica, arial, sans-serif',
            'HelveticaNeueLTPro-Md': 'dutti-daily-bold, Helvetica, arial, sans-serif',
            'HelveticaNeueLTPro-Bd': 'dutti-daily-bold, Helvetica, arial, sans-serif',
            'HelveticaNeueLTPro-Blk': 'dutti-daily-bold, Helvetica, arial, sans-serif',
            'HelveticaNeueLTPro-It': 'dutti-daily-regular, Helvetica, arial, sans-serif',

            'MD-SERIF-B01-Black': 'massimo-daily-regular, ms-serif, serif',
            'DuttiW-Light': 'dutti-daily-light, Helvetica, arial, sans-serif',
            'DuttiW-Medium': 'dutti-daily-regular, Helvetica, arial, sans-serif',
            'DuttiW-Regular': 'dutti-daily-regular, Helvetica, arial, sans-serif',
            'DuttiW-SemiBold': 'dutti-daily-bold, Helvetica, arial, sans-serif',
            'DuttiWExtended-Bold': 'dutti-extended-bold, Helvetica, arial, sans-serif',
            'DuttiWExtended-ExtraBold': 'dutti-extended-extrabold, Helvetica, arial, sans-serif',
            'DuttiWExtended-Regular': 'dutti-extended-regular, Helvetica, arial, sans-serif',

            // nuevas fuentes que llegan en mayusculas y necesitamos en minuscula
            'MassimoDaily-Regular': 'massimo-daily-regular, ms-serif, serif',
            'DuttiDaily-Light': 'dutti-daily-light, Helvetica, arial, sans-serif',
            'DuttiDaily-Regular': 'dutti-daily-regular, Helvetica, arial, sans-serif',
            'DuttiDaily-Bold': 'dutti-daily-bold, Helvetica, arial, sans-serif',
            'DuttiExtended-Bold': 'dutti-extended-bold, arial black, sans-serif',
            'DuttiExtended-ExtraBold': 'dutti-extended-extrabold, arial black, sans-serif',
            'DuttiExtended-Regular': 'dutti-extended-regular, Helvetica, arial, sans-serif',
            'MassimoDaily-Bold': 'massimo-daily-bold, ms-serif, serif',
            'MassimoDaily-SemiBold': 'massimo-daily-semibold, ms-serif, serif',
            'MassimoDaily-Light': 'massimo-daily-light, ms-serif, serif',
            'DuttiDaily-Medium': 'dutti-daily-medium, ms-serif, serif',
            'DuttiDaily-SemiBold': 'dutti-daily-semibold, ms-serif, serif',
        };

        const regex = new RegExp(Object.keys(duttiFontsMapObj).join('|'), 'g');

        return JSON.parse(html.replace(regex, (matched) => {
            return duttiFontsMapObj[matched];
        }));
    }

    public dispatchCategoriesEvents(
        pieceName: string,
        categoryId: string,
        linkType: string,
        id: string,
        newWindow: boolean = false
    ) {
        const component = pieceName === 'piece-seo-categories' ? 'enlace_texto' : 'category_module';
        this.store.dispatch(ClickOnCmsComponentAnalytics({
            component, categoryId, linkType, id,
        }));
        this.store.dispatch(GoToCategory({ categoryId, newWindow }));
    }

    public dispatchMenuEvents(categoryId: string, linkType: string, id: string) {
        this.store.dispatch(ClickOnCmsComponentAnalytics({
            component: 'menu_module', categoryId, linkType, id,
        }));

        this.store.dispatch(OpenCategoriesInHeader({ categoryId }));
    }

    public openShopTheLookModal(productId: string, categoryId: string) {
        this.store.dispatch(GoToProduct({ productId, categoryId }));
    }

    public dispatchExternalEvent(
        categoryId: string,
        linkType: string,
        id: string,
        linkSrc: string,
        newWindow: boolean = false,
        parent?: string,
        relativeLink?: boolean,
    ) {
        // Hay que diferenciar en que caso el componente es de la home o es de cualquier otra pagina de CMS, si no, provocamos que cualquier enlace de CMS ejecute los efectos de la home
        this.store.dispatch(ClickOnCmsComponentAnalytics({
            component: 'external_module', categoryId, linkType, id, linkSrc, parent,
        }));

        if (linkSrc.startsWith('#')) {
            const url = this.window.location.pathname.split('#')[0] + linkSrc;
            this.store.dispatch(GoTo({ url }));
            return;
        }

        if (!linkSrc.includes('customEvent')) {
            // this.store
            //     .select(getLastHomeExternalEvent)
            //     .pipe(filter((val) => !!val), first())
            //     .subscribe(() => {
            //
            //     });
            this.store.dispatch(CheckLinkType({
                url: linkSrc, newWindow: newWindow, reload: false, relativeLink: relativeLink,
            }));
        }
    }

    public dispatchSectionEvents(
        categoryId: string,
        linkType: string,
        id: string,
        linkSrc: string,
        newWindow: boolean = false,
        parent?: string
    ): void {
        this.store.dispatch(ClickOnCmsComponentAnalytics({
            component: 'section_module', categoryId, linkType, id, linkSrc, parent,
        }));
        this.store.dispatch(GoToSection({ url: linkSrc, newWindow, reload: false }));
    }

    public reloadSlugs(slugs: string[]): void {
        this.store.dispatch(RemoveAllSlugs());
        for (const slug of slugs) {
            this.store.dispatch(LoadSlug({ slug }));
        }
    }

    // Set params for cms page request
    // TODO ECOMDUTI-20989 METODO DUPLICADO api-cms-render.service.ts
    public setRequestParams(isVip, date?): RequestParam[] {
        let requestParams: RequestParam[] = [];
        if (isVip) requestParams.push({ key: 'userFlags', value: 'vip' });
        if (!!date) requestParams.push({ key: 'timeTravelTo', value: date });
        return requestParams;
    }

    // Transform cms content response if necessary
    public parseResponseContent(content: Record<string, any>): Record<string, ContentItem> {
        const parsedContent = deepClone(content);
        Object.keys(parsedContent).forEach((item) => {
            // We need to replace helvetica to dutti font for old pages
            if (!!parsedContent[item]?.settings?.general?.text) {
                parsedContent[item].settings.general.text = this.replaceFonts(parsedContent[item].settings.general.text);
            }
        });
        return parsedContent;
    }

    // Populate Array with product ID's from slices
    public getResponseProductsId(content: Record<string, any>): string[] {
        const productIds = [];
        Object.keys(content).forEach((item) => {
            if (content[item]?.settings?.general?.link?.type === 'product') {
                productIds.push(content[item]?.settings?.general?.link.productId);
            }
        });
        return productIds;
    }

    public rebuildContent(
        el: ContentItem,
        content: Record<string, ContentItem>,
        slug: string,
        layout: string
    ): ContentItemTree {
        const _el: any = deepClone(el);
        const keys = Object.keys(_el.children);
        /*
        * Llenado de slots
        * no siempre coincide la cantidad de hijos con la propiedad numSlots
        * ejemplo:
        * children: {2:[...],3:[...]} y el numSlots es 4 (esto pasa cuando quieren empujar contenido a la derecha y
        * solo definen estilos)
        * entonces debemos pasarle null a la key 1 y a la key 4
        * children: {1:[],2:[...],3:[...], 4[]}
        *
        * en caso del switch-content children: {'slot-nav-a':[],'slot-nav-b':[...]}
        * */
        const numSlots = Math.max(keys.length, el.settings?.general?.numSlots ?? 0, (el.settings?.slots ?? []).length);
        let rebuild = [];
        if (numSlots > 0) {
            for (let i = 1; i <= numSlots; i++) {
                if (_el.definitionId === 'piece-switch-content') {
                    if (!!_el.children[i]) {
                        rebuild.push(...this.rebuildContentChild(_el.children[i] ?? [], content, slug, layout));
                    } else {
                        let _key = i === 1 ? 'slot-nav-a' : 'slot-nav-b';
                        rebuild.push(...this.rebuildContentChild(_el.children[_key] ?? [], content, slug, layout));
                    }
                } else if (_el.definitionId === 'layout-vertical-slider') {
                    if (!!_el.children[i]) {
                        let k;
                        switch (i) {
                            case 0:
                                k = 'header';
                                break;
                            case 1:
                                k = 'body';
                                break;
                            case 2:
                                k = 'footer';
                                break;
                        }
                        rebuild[k] = this.rebuildContentChild(_el.children[i] ?? [], content, slug, layout);
                    }
                } else {
                    rebuild.push(...this.rebuildContentChild(_el.children[i] ?? [], content, slug, layout));
                }
            }
        }
        _el.children = rebuild; // rebuild parentNode
        return _el;
    }

    public rebuildContentChild(childrenKeys: string[], content, slug: string, layout: string): any[] {
        const newContent = [];
        if (childrenKeys.length === 0) {
            return [null]; // esto llena espacios en blanco para que no sea ignorado en el render
        }
        childrenKeys.forEach(childKey => {
            let contenido = content[childKey];
            if (!!contenido) {
                let _contenido = this.rebuildContent(contenido, content, slug, layout);
                _contenido.slug = slug;
                _contenido.layout = layout;
                newContent.push(_contenido);
            }
        });
        return newContent;
    }

    // Execute the necessary actions before rendering the elements in the dom to improve performance
    public preRenderActions(content, context, slug): Record<string, ContentItem> {
        let _content = deepClone(content);
        const styles = {
            default: '',
            styleLG: '',
            styleMD: '',
            styleSM: '',
            styleXS: '',
        };

        Object.keys(_content).forEach(key => {
            let c = _content[key];
            if (c.definitionId === 'slice-flex') {
                const keys = Object.keys(c.children);
                const numSlots = Math.max(keys.length, c.settings?.general?.numSlots ?? 0, (c.settings?.slots ?? []).length);
                for (let childIndex = 1; childIndex <= numSlots; childIndex++) {
                    if (!c.children[childIndex] || c.children[childIndex]?.length === 0) {
                        const id = v4();
                        c.children[childIndex] = [id];
                        _content[id] = { id, definitionId: 'slice-flex', children: [] };
                    }
                }
            }
        });

        Object.keys(_content).forEach(key => {
            const contenido = _content[key];
            if (contenido.definitionId === 'piece-countdown') {
                _content[key] = this.preloadCountdown(
                    contenido,
                    context,
                    slug,
                    context.store.countryCode,
                    context.currentDate
                );
            }

            const slotStyles = CmsRenderStylesUtils.preloadStylesContainer(contenido);
            styles.default = styles.default + slotStyles.default;
            styles.styleLG = styles.styleLG + slotStyles.styleLG;
            styles.styleMD = styles.styleMD + slotStyles.styleMD;
            styles.styleSM = styles.styleSM + slotStyles.styleSM;
            styles.styleXS = styles.styleXS + slotStyles.styleXS;

            if (contenido.definitionId === 'piece-product-carousel') {
                this.store.dispatch(LoadPieceProductCarouselModule()); // Cargamos el modulo necesario de manera lazy
            }
        });

        const cmsStyles = this.document.createElement('style');
        cmsStyles.innerHTML = `
            /* parent */
            ${styles.default}
            /* structure */
            ${styles.styleLG}
            ${styles.styleMD}
            ${styles.styleSM}
            ${styles.styleXS}
        `;
        this.document.head.appendChild(cmsStyles);

        return _content;
    }

    // Calc if countdown is on time and dispatch redux action before rendering on dom (For switch content performance)
    public preloadCountdown(contenido, context, slug, countryCode: string, currentDate: DuttiDate): ContentItem {
        const preloadedCountdownContent = contenido;
        const contDownSettings: CountdownDate = {
            startDate: preloadedCountdownContent.settings.general.startDate,
            endDate: preloadedCountdownContent.settings.general.endDate,
            fromHour: preloadedCountdownContent.settings.general.fromHour,
            fromMinutes: preloadedCountdownContent.settings.general.fromMinutes,
        };

        if (preloadedCountdownContent.settings.general.dateFormat === CountDownByConfigType) {
            const config = context.store.cmsConfigurations
                .find((_config: any) => _config.key === preloadedCountdownContent.settings.general.configName);
            if (!!config) {
                contDownSettings.endDate = config.webApp;
            }
        }

        this.countDownService.initializeService(
            preloadedCountdownContent.settings.general.dateFormat,
            preloadedCountdownContent.settings.general.configName,
            countryCode,
            currentDate,
            contDownSettings
        );
        const difference = this.countDownService.getTimeDifference();

        const isOnTime = this.countDownService.generateCountDownRender(
            difference,
            preloadedCountdownContent.settings.general.onlyHours
        ).isOnTime;

        preloadedCountdownContent.settings.general.isOnTime = isOnTime;
        this.store.dispatch(ShowCountdown({ showCountdown: isOnTime, slug: slug }));

        return preloadedCountdownContent;
    }

    public checkSlugForPageView(slug: string): boolean {
        return slug === 'intro-member' || slug === 'member-get-member';
    }

    public getPageViewEventBySlug(slug: string): any {
        switch (slug) {
            case 'intro-member':
            case 'member-get-member':
                return PageViewEvent({
                    page: 'landing_membergetmember',
                    title: slug === 'intro-member' ? 'intro_member' : this.route.snapshot.queryParamMap.get('sponsorcode'),
                    section: 'corporativo',
                    eventFrom: EventFrom.sponsorship,
                });
            default:
                break;
        }
        return NoOpAction();
    }

    public checkLinkUrl(action, baseUrl): void {
        let slug = action.url;
        if (!action.relativeLink || slug.includes('mailto:') || slug.includes('tel:') || slug.includes('http')) {
            this.store.dispatch(GoToExternalPage({ url: slug, newWindow: action.newWindow }));
            return;
        }
        slug = action.url.replace(baseUrl, '');
        this.store.dispatch(GoToInternalPage({ url: slug, newWindow: action.newWindow, reload: false }));
    }

    public dispatchManagement(event) {
        // TODO ECOMDUTI-20989 TODA ESTA LOGICA DEBE IR A APOLLO
        if (event?.detail?.extra) {
            const url: any = new URL(event.detail.extra.src, 'https://www.massimodutti.com/');
            // TODO ECOMDUTI-20989 a ver si se puede abstraer
            if (event.detail.extra.type === 'external' && url.searchParams.get('customEvent') === 'openAccessibilityModal') {
                this.store.dispatch(LoadAccessibility({ openDialog: true }));
                return;
            }
            if (event.detail.extra.type === 'external' && url.searchParams.get('customEvent') === 'redirectHelp') {
                // this.store.dispatch(GoToSection({ url: 'help' }));
                this.$sliceCmsEvent.next(event.detail.extra.src);
            }
            if (event.detail.extra.type === 'external' && url.searchParams.get('customEvent') === 'newsletterSubscribe') {
                // this.store.dispatch(HomeClickOnNewsletter());
                this.store.dispatch(GoToSection({ url: 'newsletterSubscribe' }));
            }
            if (url.searchParams.get('customEvent') === 'openSearch') {
                this.store.dispatch(OpenSearchFromHeader({ from: 'header' }));
            }
            if (url.searchParams.get('customEvent') === 'goBack') {
                this.store.dispatch(GoBack());
            }

            if (this.window?.OneTrust && url.searchParams.get('customEvent') === 'openCookieSettings') {
                this.window.OneTrust.ToggleInfoDisplay();
                return;
            }
            // OJO todo lo que dependa de este effecto no funcionará sin Legacy (DEPRECATED)
            if (url.searchParams.get('customEvent') === 'closeBanner') {
                this.store.dispatch(CloseBanner());
                return;
            }
            if (url.searchParams.get('customEvent') === 'downloadApp') {
                this.store.dispatch(OpenDownloadAppLink());
                return;
            }

            const nameEventLegacy = 'legacy-event';
            this.dispatchEvent(nameEventLegacy, event.detail.extra);
        }
    }

    private dispatchEvent(name, extra) {
        const options = {
            bubbles: true,
            composed: true,
            detail: {
                extra,
            },
        };
        const exampleEvent = new CustomEvent(name, options);
        this.document.dispatchEvent(exampleEvent);
    }
}
